import { createSlice } from '@reduxjs/toolkit';

import { IAdvancedFilterValueOptions } from 'common/components/Filters/AdvancedFilters';
import { ViewType } from 'common/constants';

import {
	IEntityDataCount,
	IGraph,
} from '../../../features/RiskProfile/Taxonomy/api/type-def';

import type { PayloadAction } from '@reduxjs/toolkit';

const { CARD } = ViewType;

interface IFilterParam {
	key: string;
	values: string[];
}
interface ITaxonomyState {
	citationData: any;
	mappingGraphData: IGraph;
	riskRefSearchText: string;
	riskRefTaxonomyFilters: any;
	applyFilterFlag: boolean;
	fullUpdateText: string;
	applyFilterEnabled: boolean;
	productsData: any;
	insititutionData: any;
	jurisdictionData: any;
	taxonomyDrawerState: boolean;
	taxonomyfilterKeys: any;
	taxonomyAppliedfilterKeys: any;
	taxonomySelectedAppliedfilterKeys: IAdvancedFilterValueOptions[];
	riskRefTaxonomySourceFilters: IFilterParam[];
	riskRefTaxonomySourceFiltersKey: string;
	riskRefTaxonomySourceFilterLabel: string;
	riskRefTaxonomySourceFiltersKeyFlag: boolean;
	taxonomyViewTypeValue: string;
	riskRefTaxonomyModalFlag: boolean;
	entityResponseData: any[];
	entityCountData: IEntityDataCount[];
	originalEntityResponseData: any[];
	allEntityResponseData: any[];
	allOriginalEntityResponseData: any[];
	isPRCEditDataDeleting: boolean;
	isRegenerateMappingBtnClicked: boolean;
	addedRelationshipMappingIds: string[];
	isSubmitChangesButtonDisabled: boolean;
}

const initialState: ITaxonomyState = {
	citationData: {},
	mappingGraphData: {} as IGraph,
	riskRefSearchText: '',
	riskRefTaxonomyFilters: [],
	applyFilterFlag: false,
	fullUpdateText: '',
	applyFilterEnabled: false,
	productsData: [],
	insititutionData: [],
	jurisdictionData: [],
	taxonomyDrawerState: false,
	taxonomyfilterKeys: [],
	taxonomyAppliedfilterKeys: [],
	taxonomySelectedAppliedfilterKeys: [],
	riskRefTaxonomySourceFilters: [],
	riskRefTaxonomySourceFiltersKey: '',
	riskRefTaxonomySourceFiltersKeyFlag: false,
	riskRefTaxonomySourceFilterLabel: '',
	taxonomyViewTypeValue: CARD,
	riskRefTaxonomyModalFlag: false,
	entityResponseData: [],
	entityCountData: [],
	originalEntityResponseData: [],
	allEntityResponseData: [],
	allOriginalEntityResponseData: [],
	isPRCEditDataDeleting: false,
	isRegenerateMappingBtnClicked: false,
	addedRelationshipMappingIds: [],
	isSubmitChangesButtonDisabled: false,
};

export const taxonomySlice = createSlice({
	name: 'taxonomy',
	initialState,
	reducers: {
		setCitationDataHandler: (state, action: PayloadAction<any>) => {
			state.citationData = action.payload;
		},
		setMappingGraphHandler: (state, action: PayloadAction<IGraph>) => {
			state.mappingGraphData = action.payload;
		},
		setRiskRefSearchText: (state, action: PayloadAction<string>) => {
			state.riskRefSearchText = action.payload;
		},
		setRiskRefTaxonomyFilter: (state, action: PayloadAction<any[]>) => {
			state.riskRefTaxonomyFilters = action.payload;
		},
		setRiskRefApplyTaxonomyFilterFlag: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.applyFilterFlag = action.payload;
		},
		setFullUpdateText: (state, action: PayloadAction<string>) => {
			state.fullUpdateText = action.payload;
		},
		setRiskRefApplyFilterEnabled: (state, action: PayloadAction<boolean>) => {
			state.applyFilterEnabled = action.payload;
		},
		setProductsDataObj: (state, action: PayloadAction<any>) => {
			state.productsData = action.payload;
		},
		setInstitutionDataObj: (state, action: PayloadAction<any>) => {
			state.insititutionData = action.payload;
		},
		setJurisdictionDataObj: (state, action: PayloadAction<any>) => {
			state.jurisdictionData = action.payload;
		},
		setTaxonomyDrawerState: (state, action: PayloadAction<boolean>) => {
			state.taxonomyDrawerState = action.payload;
		},
		setTaxonomyFilterKeys: (state, action: PayloadAction<any>) => {
			state.taxonomyfilterKeys = action.payload;
		},
		setTaxonomyAppliedFilterList: (state, action: PayloadAction<any>) => {
			state.taxonomyAppliedfilterKeys = action.payload;
		},
		setTaxonomySelectedAppliedFilterList: (
			state,
			action: PayloadAction<IAdvancedFilterValueOptions[]>,
		) => {
			state.taxonomySelectedAppliedfilterKeys = action.payload;
		},
		setRiskRefTaxonomySourceFilters: (
			state,
			action: PayloadAction<IFilterParam[]>,
		) => {
			state.riskRefTaxonomySourceFilters = action.payload;
		},
		setRiskRefTaxonomySourceFiltersKey: (
			state,
			action: PayloadAction<string>,
		) => {
			state.riskRefTaxonomySourceFiltersKey = action.payload;
		},
		setRiskRefTaxonomySourceFiltersKeyFlag: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.riskRefTaxonomySourceFiltersKeyFlag = action.payload;
		},
		setRiskRefTaxonomySourceFilterLabel: (
			state,
			action: PayloadAction<string>,
		) => {
			state.riskRefTaxonomySourceFilterLabel = action.payload;
		},
		setTaxonomyViewTypeValue(state, action: PayloadAction<string>) {
			state.taxonomyViewTypeValue = action.payload;
		},
		setRiskRefTaxonomyModalFlag: (state, action: PayloadAction<boolean>) => {
			state.riskRefTaxonomyModalFlag = action.payload;
		},
		setEntityResponseData: (state, action: PayloadAction<any[]>) => {
			state.entityResponseData = action.payload;
		},
		setEntityCountData: (state, action: PayloadAction<IEntityDataCount[]>) => {
			state.entityCountData = action.payload;
		},
		setOriginalEntityResponseData: (state, action: PayloadAction<any[]>) => {
			state.originalEntityResponseData = action.payload;
		},
		setAllEntityResponseData: (state, action: PayloadAction<any[]>) => {
			state.allEntityResponseData = action.payload;
		},
		setAllOriginalEntityResponseData: (state, action: PayloadAction<any[]>) => {
			state.allOriginalEntityResponseData = action.payload;
		},
		setIsPRCEditDataDeleting: (state, action: PayloadAction<boolean>) => {
			state.isPRCEditDataDeleting = action.payload;
		},
		setIsRegenerateMappingBtnClicked: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.isRegenerateMappingBtnClicked = action.payload;
		},
		setAddedRelationshipMappingIds: (
			state,
			action: PayloadAction<string[]>,
		) => {
			state.addedRelationshipMappingIds = action.payload;
		},
		setIsSubmitChangesButtonDisabled: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.isSubmitChangesButtonDisabled = action.payload;
		},
	},
});
export const {
	setCitationDataHandler,
	setMappingGraphHandler,
	setRiskRefSearchText,
	setRiskRefTaxonomyFilter,
	setRiskRefApplyTaxonomyFilterFlag,
	setFullUpdateText,
	setRiskRefApplyFilterEnabled,
	setProductsDataObj,
	setInstitutionDataObj,
	setJurisdictionDataObj,
	setTaxonomyDrawerState,
	setTaxonomyFilterKeys,
	setTaxonomyAppliedFilterList,
	setTaxonomySelectedAppliedFilterList,
	setRiskRefTaxonomySourceFilters,
	setRiskRefTaxonomySourceFiltersKey,
	setRiskRefTaxonomySourceFiltersKeyFlag,
	setRiskRefTaxonomySourceFilterLabel,
	setTaxonomyViewTypeValue,
	setRiskRefTaxonomyModalFlag,
	setEntityResponseData,
	setEntityCountData,
	setOriginalEntityResponseData,
	setAllEntityResponseData,
	setAllOriginalEntityResponseData,
	setIsPRCEditDataDeleting,
	setIsRegenerateMappingBtnClicked,
	setAddedRelationshipMappingIds,
	setIsSubmitChangesButtonDisabled,
} = taxonomySlice.actions;
export default taxonomySlice.reducer;
